/**
 * Why stock left, or was corrected. "Removed" used to cover a pack sold over the
 * counter, a pack dropped on the floor and a pack nobody could find, which made
 * waste impossible to report on — the reason is what tells them apart.
 *
 * Shared by the API (which stores it on the movement) and the web app (which
 * offers the list), so both agree on the values and their names.
 */

export const STOCK_REASONS = [
  'sold',
  'damaged',
  'spoiled',
  'lost',
  'donated',
  'sample',
  'staff_use',
  'shrink',
  'count_variance',
  'correction',
  'other',
] as const;

export type StockReason = (typeof STOCK_REASONS)[number];

export const STOCK_REASON_LABELS: Record<StockReason, string> = {
  sold: 'Sold',
  damaged: 'Damaged',
  spoiled: 'Spoiled',
  lost: 'Lost',
  donated: 'Donated',
  sample: 'Sample',
  staff_use: 'Staff use',
  shrink: 'Trim / shrink',
  count_variance: 'Count variance',
  correction: 'Correction',
  other: 'Other',
};

/**
 * The reasons an operator picks from when taking stock out by hand. A count
 * variance is only ever written by a stocktake, and a correction by an edit.
 */
export const SCAN_OUT_REASONS: readonly StockReason[] = [
  'sold',
  'damaged',
  'spoiled',
  'lost',
  'donated',
  'sample',
  'staff_use',
  'shrink',
  'other',
];

/** Reasons that are a loss to the plant, as opposed to a sale or a fix. */
export const STOCK_LOSS_REASONS: readonly StockReason[] = [
  'damaged',
  'spoiled',
  'lost',
  'donated',
  'sample',
  'staff_use',
  'shrink',
  'count_variance',
  'other',
];

/**
 * Narrows a client-supplied string to a known reason.
 * @param value - The value to check
 * @returns Whether it's one of {@link STOCK_REASONS}
 */
export function isStockReason(value: unknown): value is StockReason {
  return (
    typeof value === 'string' &&
    (STOCK_REASONS as readonly string[]).includes(value)
  );
}

/**
 * A reason's display name, falling back to the raw value for one this build
 * doesn't know.
 * @param reason - The stored reason
 * @returns Its label, or an empty string when there's none
 */
export function stockReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '';
  return isStockReason(reason) ? STOCK_REASON_LABELS[reason] : reason;
}
